import { useMemo, useState } from "react";
import { ChevronDown, ChevronUp, PenLine } from "lucide-react";
import { formatDollars } from "../../lib/draftCampaign";

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "mra_jwt";
const fileSrc = (id) => {
  const t = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
  return `${BACKEND_ORIGIN}/api/files/${id}${t ? `?auth=${t}` : ""}`;
};

export const FIELD_LABELS = {
  title: "Title", story: "Funding need", category: "Type of need", school: "School", program: "Program", goal_cents: "Goal",
  cover_file_id: "Profile photo", intro_video_id: "Video", request_doc_file_id: "Document", full_name: "Name",
  grad_year: "Grad year", location: "Location", headline: "Headline", bio: "Bio", deadline: "Deadline",
  photo_file_ids: "Photos", visibility: "Visibility",
};
const LONG_TEXT = new Set(["story", "bio", "headline", "title"]);

// Word-level LCS diff; small inputs only (campaign copy is a few hundred words).
export const wordDiff = (a = "", b = "") => {
  const A = String(a || "").split(/(\s+)/).filter(Boolean);
  const B = String(b || "").split(/(\s+)/).filter(Boolean);
  if (A.length * B.length > 250000) return { before: [{ t: a, k: "same" }], after: [{ t: b, k: "same" }] };
  const dp = Array.from({ length: A.length + 1 }, () => new Uint16Array(B.length + 1));
  for (let i = A.length - 1; i >= 0; i--) for (let j = B.length - 1; j >= 0; j--) {
    dp[i][j] = A[i] === B[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  }
  const before = [], after = [];
  let i = 0, j = 0;
  while (i < A.length && j < B.length) {
    if (A[i] === B[j]) { before.push({ t: A[i], k: "same" }); after.push({ t: B[j], k: "same" }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { before.push({ t: A[i], k: "del" }); i++; }
    else { after.push({ t: B[j], k: "add" }); j++; }
  }
  while (i < A.length) before.push({ t: A[i++], k: "del" });
  while (j < B.length) after.push({ t: B[j++], k: "add" });
  return { before, after };
};

const Marked = ({ parts, empty }) => (
  <p className="whitespace-pre-line text-sm leading-relaxed text-ink">
    {parts.length === 0 ? <em className="italic text-olive">{empty}</em> : parts.map((p, i) => (
      p.k === "same" ? <span key={i}>{p.t}</span>
        : <mark key={i} className={`rounded-sm px-0.5 ${p.k === "del" ? "bg-scarlet/15 text-scarlet line-through decoration-scarlet/60" : "bg-grass/20 text-ink"}`}>{p.t}</mark>
    ))}
  </p>
);

const Thumbs = ({ ids, highlight, tone }) => (
  <div className="flex flex-wrap gap-1.5">
    {(ids || []).length === 0 && <em className="text-sm italic text-olive">none</em>}
    {(ids || []).map((id) => (
      <img key={id} src={fileSrc(id)} alt="" className={`h-14 w-14 rounded-md object-cover ${highlight.has(id) ? `ring-2 ring-offset-1 ring-offset-bone ${tone}` : "opacity-70"}`} />
    ))}
  </div>
);

const Scalar = ({ field, value }) => {
  if (value === null || value === undefined || value === "" || (Array.isArray(value) && value.length === 0)) {
    return <em className="text-sm italic text-olive">empty</em>;
  }
  if (field === "goal_cents") return <span className="font-serif text-2xl text-ink">{formatDollars(value)}</span>;
  if (field === "visibility") return <span className="text-sm text-ink">{value === "private" ? "Private, link only" : "Public, discoverable"}</span>;
  if (field === "cover_file_id") return <img src={fileSrc(value)} alt="" className="h-20 w-20 rounded-md object-cover" />;
  if (field === "request_doc_file_id") return <a href={fileSrc(value)} target="_blank" rel="noreferrer" className="text-sm font-semibold text-ink underline underline-offset-2">Open document</a>;
  if (field === "intro_video_id") return <span className="text-sm text-ink">Video attached</span>;
  return <span className="text-sm text-ink">{String(value)}</span>;
};

const Side = ({ field, value, other, side }) => {
  if (field === "photo_file_ids") {
    const mine = new Set(value || []), theirs = new Set(other || []);
    const changed = new Set([...mine].filter((id) => !theirs.has(id)));
    return <Thumbs ids={value} highlight={changed} tone={side === "before" ? "ring-scarlet" : "ring-grass"} />;
  }
  return <Scalar field={field} value={value} />;
};

const FieldDiff = ({ field, before, after, testid }) => {
  const isText = LONG_TEXT.has(field);
  const d = useMemo(() => (isText ? wordDiff(before, after) : null), [isText, before, after]);
  return (
    <div className="grid grid-cols-1 gap-x-6 gap-y-2 border-t border-border/70 py-4 first:border-t-0 md:grid-cols-[140px_1fr_1fr]" data-testid={testid}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-olive md:pt-1">{FIELD_LABELS[field] || field}</p>
      <div>
        <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-scarlet/80 md:hidden">Live now</p>
        {isText ? <Marked parts={d.before} empty="empty" /> : <Side field={field} value={before} other={after} side="before" />}
      </div>
      <div>
        <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-grass md:hidden">Proposed</p>
        {isText ? <Marked parts={d.after} empty="empty" /> : <Side field={field} value={after} other={before} side="after" />}
      </div>
    </div>
  );
};

/**
 * EditDiff — before/after view of a student's pending edits to a live page.
 * Text fields get word-level highlighting; money, photos, visibility and
 * documents render as what they are instead of raw ids.
 */
export const EditDiff = ({ c, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  const fields = c.changed_fields || [];
  if (c.kind !== "edit" || fields.length === 0) return null;
  const labels = fields.map((k) => FIELD_LABELS[k] || k);
  return (
    <div className="mt-3 rounded-lg border border-cobalt/25 bg-cobalt/[0.04]" data-testid={`review-diff-${c.id}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid={`review-diff-toggle-${c.id}`}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left">
        <span className="flex items-center gap-2 text-xs font-semibold text-cobalt">
          <PenLine className="h-3.5 w-3.5" strokeWidth={2.2} />
          {fields.length} field{fields.length === 1 ? "" : "s"} changed
          <span className="font-normal text-olive">· {labels.join(", ")}</span>
        </span>
        {open ? <ChevronUp className="h-4 w-4 text-olive" /> : <ChevronDown className="h-4 w-4 text-olive" />}
      </button>
      {open && (
        <div className="border-t border-cobalt/20 px-4 pb-2">
          <div className="hidden grid-cols-[140px_1fr_1fr] gap-x-6 pt-3 md:grid">
            <span />
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-scarlet/80">Live now</p>
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-grass">Proposed</p>
          </div>
          {fields.map((k) => (
            <FieldDiff key={k} field={k} before={c.current?.[k]} after={c[k]} testid={`review-diff-field-${c.id}-${k}`} />
          ))}
        </div>
      )}
    </div>
  );
};
